import { useState, useEffect, useCallback } from 'react'
import { Movie, FormattedMovie } from '../types/movie'
import { getOldMovies, getOldMoviesCount, urlFor } from '../utils/sanityClient'

const formatMovie = (movie: Movie): FormattedMovie => ({
  ...movie,
  poster: urlFor(movie.poster),
  releaseDate: new Date(movie.releaseDate).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }),
  hypeScore: movie.hypeLevel
})

export function useOldMovies(limit: number = 20) {
  const [movies, setMovies] = useState<FormattedMovie[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [hasMore, setHasMore] = useState(true)

  const fetchMovies = useCallback(async (page: number = 0, append: boolean = false) => {
    try {
      setLoading(true)
      const [data, count] = await Promise.all([
        getOldMovies(page, limit),
        page === 0 ? getOldMoviesCount() : Promise.resolve(totalCount)
      ])
      
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format from Sanity')
      }

      const formattedMovies = data.map(formatMovie)
      
      if (append) {
        setMovies(prev => [...prev, ...formattedMovies])
      } else {
        setMovies(formattedMovies)
      }
      
      if (page === 0) {
        setTotalCount(count)
      }
      
      setHasMore((page + 1) * limit < count)
      setCurrentPage(page)
      setLoading(false)
    } catch (err) {
      console.error('Error fetching old movies:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch old movies'))
      setLoading(false)
    }
  }, [limit, totalCount])

  useEffect(() => {
    fetchMovies(0, false)
  }, [fetchMovies])

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchMovies(currentPage + 1, true)
    }
  }, [loading, hasMore, currentPage, fetchMovies])

  const goToPage = useCallback((page: number) => {
    if (page >= 0 && page !== currentPage) {
      fetchMovies(page, false)
    }
  }, [currentPage, fetchMovies])

  return {
    movies,
    loading,
    error,
    currentPage,
    totalCount,
    hasMore,
    totalPages: Math.ceil(totalCount / limit),
    loadMore,
    goToPage,
    refresh: () => {
      setCurrentPage(0)
      fetchMovies(0, false)
    }
  }
}